import { DECK, SUITS, winningPlay } from "./engine.js";
import {
  PRACTICE_SEATS,
  advancePracticeState,
  applyBid,
  applyCardPlay,
  chooseAiBid,
  chooseAiCard,
  createPracticeDeal,
  legalCardIds,
  serializePracticeDeal,
} from "./practice-engine.js";
import {
  enrichPlayDecision,
  loadPracticeHistory,
  savePracticeDeal,
  summarizePracticeStyle,
} from "./practice-learning.js";
import { suitName, t } from "./i18n.js";

const AI_DELAY = 750;
const TRICK_DELAY = 850;
const MATCH_TARGET = 500;
const NEXT_DEAL_DELAY = 1600;
const SUIT_ORDER = new Map(SUITS.map((suit, index) => [suit.id, index]));
const RANK_ORDER = new Map(["7", "8", "9", "J", "Q", "K", "10", "A"].map((rank, index) => [rank, index]));
const SPRITE_COLUMNS = Object.freeze({ "7": 0, "8": 1, "9": 2, "10": 3, J: 4, Q: 5, K: 6, A: 7 });
const SPRITE_ROWS = Object.freeze({ clubs: 0, diamonds: 1, spades: 2, hearts: 3 });

function suitInfo(suitId) {
  const suit = SUITS.find((item) => item.id === suitId);
  return suit ? { ...suit, name: suitName(suit.id) } : null;
}

function seatLabel(seat) {
  return t(`practice.seat.${PRACTICE_SEATS[seat].key}`);
}

function cardById(id) {
  return DECK.find((card) => card.id === id);
}

function cardLabel(card) {
  const suit = suitInfo(card.suit);
  return `${card.rank}${suit.symbol}`;
}

function sortedHand(hand) {
  return [...hand].sort((first, second) =>
    SUIT_ORDER.get(first.suit) - SUIT_ORDER.get(second.suit)
    || RANK_ORDER.get(first.rank) - RANK_ORDER.get(second.rank));
}

function percent(value) {
  return value == null ? t("common.none") : `${Math.round(value * 100)} %`;
}

function cardMarkup(card, { button = false, legal = true, interactive = button } = {}) {
  const suit = suitInfo(card.suit);
  const tag = button ? "button" : "span";
  const spriteX = (SPRITE_COLUMNS[card.rank] / 7) * 100;
  const spriteY = (SPRITE_ROWS[card.suit] / 3) * 100;
  const attributes = button
    ? `type="button" data-practice-card="${card.id}" aria-label="${t("app.cardLabel", { rank: card.rank, suit: suit.name })}" ${interactive && legal ? "" : "disabled"}`
    : `aria-label="${t("app.cardLabel", { rank: card.rank, suit: suit.name })}"`;
  const stateClass = interactive ? (legal ? "" : "is-disabled") : "is-static";
  return `<${tag} class="practice-card ${suit.red ? "is-red" : ""} ${stateClass}" style="--card-x:${spriteX}%;--card-y:${spriteY}%" ${attributes}>
    <strong>${card.rank}</strong><span>${suit.symbol}</span>
  </${tag}>`;
}

function historyLabel(item) {
  const player = Number.isInteger(item.seat) ? seatLabel(item.seat) : "";
  if (item.type === "deal") return t("practice.history.deal", { deal: item.dealNumber, dealer: seatLabel(item.dealer) });
  if (item.type === "bid") {
    return item.action === "pass"
      ? t("practice.history.pass", { player })
      : t("practice.history.take", { player, suit: suitName(item.suit) });
  }
  if (item.type === "play") return t("practice.history.play", { player, card: cardLabel(cardById(item.card)) });
  if (item.type === "trick") return t("practice.history.trick", { player, points: item.points });
  if (item.type === "result") return t(item.outcome === "win" ? "practice.history.win" : "practice.history.loss");
  return "";
}

export function createPracticeTable({ onDealComplete } = {}) {
  const elements = {
    launch: document.querySelector("#practice-launch"),
    dialog: document.querySelector("#practice-dialog"),
    close: document.querySelector("#practice-close"),
    newDeal: document.querySelector("#practice-new-deal"),
    scoreUs: document.querySelector("#practice-score-us"),
    scoreThem: document.querySelector("#practice-score-them"),
    contract: document.querySelector("#practice-contract"),
    upcard: document.querySelector("#practice-upcard"),
    trick: document.querySelector("#practice-trick"),
    livePoints: document.querySelector("#practice-live-points"),
    previousTrick: document.querySelector("#practice-previous-trick"),
    hand: document.querySelector("#practice-hand"),
    actions: document.querySelector("#practice-actions"),
    status: document.querySelector("#practice-status"),
    history: document.querySelector("#practice-history"),
    profile: document.querySelector("#practice-profile"),
    players: PRACTICE_SEATS.map((seat) => document.querySelector(`#practice-player-${seat.id}`)),
  };
  if (!elements.dialog || !elements.launch) return null;

  let deal = null;
  let dealNumber = loadPracticeHistory().length + 1;
  let cumulative = [0, 0];
  let timer = null;
  let matchFinished = false;
  const completed = new Set();

  function clearTimer() {
    if (timer) window.clearTimeout(timer);
    timer = null;
  }

  function playerStatus(seat) {
    if (!deal) return t("practice.waiting");
    return deal.currentSeat === seat ? t("practice.toPlay") : "";
  }

  function renderPlayers() {
    PRACTICE_SEATS.forEach((seat) => {
      const element = elements.players[seat.id];
      element.classList.toggle("is-active", deal?.currentSeat === seat.id);
      const takerSuit = deal?.takerSeat === seat.id ? suitInfo(deal.trump) : null;
      element.classList.toggle("is-taker", Boolean(takerSuit));
      element.classList.toggle("has-red-trump", Boolean(takerSuit?.red));
      const dealerBadge = deal?.dealer === seat.id
        ? `<b class="practice-dealer-badge" aria-label="${t("practice.dealerAria")}">D</b>`
        : "";
      const takerBadge = takerSuit
        ? `<b class="practice-taker-suit" aria-label="${t("practice.trumpAria", { suit: takerSuit.name })}">${takerSuit.symbol}</b>`
        : "";
      const status = playerStatus(seat.id);
      element.innerHTML = `${dealerBadge}${takerBadge}<strong>${seatLabel(seat.id)}</strong>${status ? `<span>${status}</span>` : ""}`;
    });
  }

  function renderContract() {
    if (!deal) {
      elements.contract.textContent = t("practice.objective", { target: MATCH_TARGET });
      return;
    }
    if (!deal.trump) {
      elements.contract.textContent = t("practice.bidding", { target: MATCH_TARGET, round: deal.bidRound });
      return;
    }
    const suit = suitInfo(deal.trump);
    elements.contract.innerHTML = t("practice.contract", {
      target: MATCH_TARGET,
      symbol: `<span class="${suit.red ? "is-red" : ""}">${suit.symbol}</span>`,
      player: seatLabel(deal.takerSeat),
    });
  }

  function renderUpcard() {
    const show = deal && deal.phase === "bidding";
    elements.upcard.innerHTML = show
      ? `<small>${t("practice.upcard")}</small>${cardMarkup(deal.center)}`
      : "";
  }

  function renderTrick() {
    if (!deal?.trick.length) {
      elements.trick.innerHTML = deal?.phase === "deal-result"
        ? `<strong class="practice-result-mark">${deal.result.userOutcome === "win" ? t("practice.won") : t("practice.lost")}</strong>`
        : "";
      return;
    }
    elements.trick.innerHTML = deal.trick.map((play) =>
      `<div class="practice-play is-seat-${play.seat}">${cardMarkup(play.card)}</div>`).join("");
  }

  function renderLivePoints() {
    if (!deal) {
      elements.livePoints.innerHTML = "";
      return;
    }
    const points = deal.result?.scoredPoints || deal.teamPoints;
    elements.livePoints.innerHTML = `<small>${t("practice.dealPoints")}</small><span><b>${t("practice.teamUs", { points: points[0] })}</b><i></i><b>${t("practice.teamThem", { points: points[1] })}</b></span>`;
  }

  function renderPreviousTrick() {
    if (!deal) {
      elements.previousTrick.innerHTML = "";
      return;
    }
    const previous = deal.phase === "trick-result"
      ? deal.tricks.at(-2)
      : deal.tricks.at(-1);
    if (!previous) {
      elements.previousTrick.innerHTML = `<small>${t("practice.previous")}</small><span class="practice-no-trick">${t("common.none")}</span>`;
      return;
    }
    elements.previousTrick.innerHTML = `
      <small>${t("practice.previous")}</small>
      <div class="practice-previous-cards">${previous.plays.map((play) => `<div class="practice-previous-play is-seat-${play.seat}">${cardMarkup(cardById(play.card))}</div>`).join("")}</div>
      <span>${t("practice.previousResult", { player: seatLabel(previous.winnerSeat), points: previous.points })}</span>`;
  }

  function renderHand() {
    if (!deal) {
      elements.hand.innerHTML = "";
      return;
    }
    const interactive = deal.phase === "play" && deal.currentSeat === 0;
    const legal = new Set(interactive ? legalCardIds(deal, 0) : []);
    elements.hand.innerHTML = sortedHand(deal.hands[0]).map((card) =>
      cardMarkup(card, { button: true, interactive, legal: !interactive || legal.has(card.id) })).join("");
  }

  function renderActions() {
    if (!deal || deal.phase !== "bidding" || deal.currentSeat !== 0) {
      elements.actions.innerHTML = "";
      return;
    }
    const suits = deal.bidRound === 1
      ? SUITS.filter((suit) => suit.id === deal.center.suit)
      : SUITS.filter((suit) => suit.id !== deal.center.suit);
    elements.actions.innerHTML = [
      ...suits.map((suit) => `<button type="button" data-practice-bid="take" data-suit="${suit.id}" class="${suit.red ? "is-red" : ""}">${t("practice.take", { symbol: suit.symbol })}</button>`),
      `<button type="button" data-practice-bid="pass" class="is-pass">${t("practice.pass")}</button>`,
    ].join("");
  }

  function renderStatus() {
    elements.status.hidden = deal?.phase === "bidding";
    if (!deal) elements.status.textContent = t("practice.status.initial");
    else if (deal.phase === "bidding") {
      elements.status.textContent = "";
    } else if (deal.phase === "play") {
      const playPosition = deal.trick.length + 1;
      const leaderName = seatLabel(deal.leader);
      elements.status.textContent = deal.currentSeat === 0
        ? playPosition === 1
          ? t("practice.yourLead", { trick: deal.trickNumber })
          : t("practice.yourPlay", { position: playPosition, leader: leaderName })
        : playPosition === 1
          ? t("practice.aiLead", { player: seatLabel(deal.currentSeat) })
          : t("practice.aiPlay", { player: seatLabel(deal.currentSeat), position: playPosition, leader: leaderName });
    } else if (deal.phase === "trick-result") {
      const winner = winningPlay(deal.trick, deal.trump);
      elements.status.textContent = t("practice.trickWinner", { player: seatLabel(winner.seat) });
    } else if (deal.phase === "passed-out") elements.status.textContent = t("practice.passedOut");
    else if (deal.phase === "deal-result") {
      elements.status.textContent = matchFinished
        ? t("practice.matchWinner", { winner: t(cumulative[0] >= MATCH_TARGET ? "practice.winnerYou" : "practice.winnerThem"), target: MATCH_TARGET, us: cumulative[0], them: cumulative[1] })
        : t("practice.dealWinner", { winner: t(deal.result.userOutcome === "win" ? "practice.winnerYou" : "practice.winnerThem") });
    }
  }

  function renderHistory() {
    if (!elements.history) return;
    const items = deal?.history.slice(-8).reverse() || [];
    elements.history.innerHTML = items.map((item) => `<li>${historyLabel(item)}</li>`).join("") || `<li>${t("practice.noAction")}</li>`;
  }

  function renderProfile() {
    if (!elements.profile) return;
    const profile = summarizePracticeStyle();
    elements.profile.innerHTML = `
      <p>${t("practice.profile.summary", { decisions: `<strong>${profile.decisions}</strong>`, deals: `<strong>${profile.deals}</strong>` })}</p>
      <dl>
        <div><dt>${t("practice.profile.direct")}</dt><dd>${percent(profile.directTakeRate)}</dd></div>
        <div><dt>${t("practice.profile.trumpLead")}</dt><dd>${percent(profile.trumpLeadRate)}</dd></div>
        <div><dt>${t("practice.profile.aceDuck")}</dt><dd>${percent(profile.aceDuckRate)}</dd></div>
        <div><dt>${t("practice.profile.tenHold")}</dt><dd>${percent(profile.tenHoldRate)}</dd></div>
        <div><dt>${t("practice.profile.trumpHold")}</dt><dd>${percent(profile.trumpHoldRate)}</dd></div>
      </dl>`;
  }

  function render() {
    elements.scoreUs.textContent = cumulative[0];
    elements.scoreThem.textContent = cumulative[1];
    renderPlayers();
    renderContract();
    renderUpcard();
    renderTrick();
    renderLivePoints();
    renderPreviousTrick();
    renderHand();
    renderActions();
    renderStatus();
    renderHistory();
    renderProfile();
  }

  function finishDeal() {
    if (!deal?.result) return;
    if (completed.has(deal.id)) {
      if (!matchFinished && !timer) timer = window.setTimeout(() => startDeal(), NEXT_DEAL_DELAY);
      return;
    }
    completed.add(deal.id);
    cumulative = cumulative.map((score, team) => score + deal.result.scoredPoints[team]);
    const serialized = serializePracticeDeal(deal);
    serialized.decisions = serialized.decisions.map((decision) => decision.type === "play" ? enrichPlayDecision(decision) : decision);
    savePracticeDeal(serialized);
    void onDealComplete?.(serialized);
    matchFinished = cumulative.some((score) => score >= MATCH_TARGET);
    elements.newDeal.textContent = matchFinished ? t("practice.newMatch") : t("practice.nextDeal");
    render();
    if (!matchFinished) timer = window.setTimeout(() => startDeal(), NEXT_DEAL_DELAY);
  }

  function drive() {
    clearTimer();
    render();
    if (!deal) return;
    if (deal.phase === "passed-out") {
      timer = window.setTimeout(() => startDeal(), 750);
      return;
    }
    if (deal.phase === "trick-result") {
      timer = window.setTimeout(() => {
        deal = advancePracticeState(deal);
        if (deal.phase === "deal-result") finishDeal();
        drive();
      }, TRICK_DELAY);
      return;
    }
    if (deal.phase === "deal-result") {
      finishDeal();
      return;
    }
    if (deal.currentSeat === 0) return;
    timer = window.setTimeout(() => {
      if (deal.phase === "bidding") {
        const seat = deal.currentSeat;
        deal = applyBid(deal, seat, chooseAiBid(deal, seat));
      } else if (deal.phase === "play") {
        const seat = deal.currentSeat;
        const card = chooseAiCard(deal, seat);
        deal = applyCardPlay(deal, seat, card.id);
      }
      drive();
    }, AI_DELAY);
  }

  function startDeal({ resetMatch = false } = {}) {
    clearTimer();
    if (resetMatch || matchFinished) {
      cumulative = [0, 0];
      matchFinished = false;
      elements.newDeal.textContent = t("practice.new");
    }
    const dealer = deal ? (deal.dealer + 1) % 4 : Math.floor(Math.random() * 4);
    deal = createPracticeDeal({ dealer, dealNumber });
    dealNumber += 1;
    drive();
  }

  function recordUserBid(action, suit) {
    if (!deal || deal.phase !== "bidding" || deal.currentSeat !== 0) return;
    const decision = {
      type: "bid",
      at: new Date().toISOString(),
      seat: 0,
      round: deal.bidRound,
      hand: deal.hands[0].map((card) => card.id),
      center: deal.center.id,
      previousBids: [...deal.bids],
      action,
      suit: suit || null,
    };
    deal = applyBid(deal, 0, { action, suit });
    deal.decisions = [...deal.decisions, decision];
    drive();
  }

  function recordUserPlay(cardId) {
    if (!deal || deal.phase !== "play" || deal.currentSeat !== 0) return;
    const decision = {
      type: "play",
      at: new Date().toISOString(),
      seat: 0,
      trickNumber: deal.trickNumber,
      handBefore: deal.hands[0].map((card) => card.id),
      legalCards: legalCardIds(deal, 0),
      trickBefore: deal.trick.map((play) => ({ seat: play.seat, card: play.card.id })),
      playedBefore: deal.played.map((play) => ({ seat: play.seat, card: play.card.id })),
      trump: deal.trump,
      takerSeat: deal.takerSeat,
      takingTeam: deal.takingTeam,
      teamPoints: [...deal.teamPoints],
      teamTricks: [...deal.teamTricks],
      chosenCard: cardId,
    };
    deal = applyCardPlay(deal, 0, cardId);
    deal.decisions = [...deal.decisions, decision];
    drive();
  }

  function open() {
    if (typeof elements.dialog.showModal === "function") elements.dialog.showModal();
    else elements.dialog.setAttribute("open", "");
    if (!deal) startDeal();
    else drive();
  }

  elements.launch.addEventListener("click", open);
  elements.close.addEventListener("click", () => {
    clearTimer();
    elements.dialog.close();
  });
  elements.dialog.addEventListener("click", (event) => {
    if (event.target === elements.dialog) {
      clearTimer();
      elements.dialog.close();
    }
  });
  elements.newDeal.addEventListener("click", () => startDeal({ resetMatch: matchFinished }));
  elements.actions.addEventListener("click", (event) => {
    const button = event.target.closest("[data-practice-bid]");
    if (button) recordUserBid(button.dataset.practiceBid, button.dataset.suit);
  });
  elements.hand.addEventListener("click", (event) => {
    const button = event.target.closest("[data-practice-card]");
    if (button && !button.disabled) recordUserPlay(button.dataset.practiceCard);
  });

  renderProfile();
  window.addEventListener("app-locale-changed", render);
  return { startDeal, getState: () => deal, open };
}
